/**
 * Reusable probe primitives.
 *
 * Everything here is deliberately side-effect free from the page's perspective and
 * bounded by a timeout — a probe that hangs would stall the whole report.
 */

import type { SignalOutcome } from './types';

export const extensionUrl = (extensionId: string, resourcePath: string) =>
  `chrome-extension://${extensionId}/${resourcePath.replace(/^\//, '')}`;

function withTimeout<T>(promise: Promise<T>, ms: number, onTimeout: () => T): Promise<T> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(onTimeout()), ms);
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      () => {
        clearTimeout(timer);
        resolve(onTimeout());
      },
    );
  });
}

/**
 * Probe a web-accessible resource by fetching it.
 *
 * Only works when the resource is readable cross-origin, which is the case for
 * resources declared with a `matches` pattern covering the current page. If the
 * extension is absent — or the resource is declared `use_dynamic_url: true`, or its
 * `matches` doesn't cover this origin — the fetch fails at the network layer and
 * throws a TypeError, which is indistinguishable from "not installed".
 *
 * That ambiguity is why `use_dynamic_url` is an effective mitigation.
 */
export async function probeFetch(
  extensionId: string,
  resourcePath: string,
  { timeoutMs = 2000 }: { timeoutMs?: number } = {},
): Promise<SignalOutcome> {
  const url = extensionUrl(extensionId, resourcePath);

  const attempt = (async (): Promise<SignalOutcome> => {
    try {
      // no-store: a cached response could outlive the extension being uninstalled and
      // report a resource as present after it stopped being served.
      const res = await fetch(url, { cache: 'no-store' });
      return res.ok
        ? { fired: true, detail: `fetch ${resourcePath} -> HTTP ${res.status}` }
        : { fired: false, detail: `fetch ${resourcePath} -> HTTP ${res.status}` };
    } catch (err) {
      return {
        fired: false,
        detail: `fetch ${resourcePath} blocked (${(err as Error).name}) — extension absent, or resource not exposed to this origin`,
      };
    }
  })();

  return withTimeout(attempt, timeoutMs, () => ({
    fired: false,
    detail: `fetch ${resourcePath} timed out after ${timeoutMs}ms`,
  }));
}

/**
 * Probe an image resource by loading it into an `<img>`.
 *
 * Unlike `probeFetch` this needs no CORS grant — the load either succeeds or errors —
 * so it can reach some resources a fetch cannot. It only distinguishes "decodable
 * image" from "not", so it's limited to image resources.
 */
export function probeImage(
  extensionId: string,
  resourcePath: string,
  { timeoutMs = 2000 }: { timeoutMs?: number } = {},
): Promise<SignalOutcome> {
  // <img> has no cache-mode control, so bust the cache via the query string. WAR
  // matching ignores the query, so this does not affect whether the resource resolves.
  const url = `${extensionUrl(extensionId, resourcePath)}?_=${Date.now()}${Math.random()}`;

  const attempt = new Promise<SignalOutcome>((resolve) => {
    const img = new Image();
    const done = (outcome: SignalOutcome) => {
      img.onload = img.onerror = null;
      img.src = '';
      resolve(outcome);
    };
    img.onload = () =>
      done({
        fired: true,
        detail: `<img> loaded ${resourcePath} (${img.naturalWidth}x${img.naturalHeight})`,
      });
    img.onerror = () =>
      done({ fired: false, detail: `<img> failed to load ${resourcePath}` });
    img.src = url;
  });

  return withTimeout(attempt, timeoutMs, () => ({
    fired: false,
    detail: `<img> ${resourcePath} timed out after ${timeoutMs}ms`,
  }));
}

/** Probe for a global defined on `window` by a content script running in the main world. */
export async function probeGlobal(name: string): Promise<SignalOutcome> {
  const present = name in (window as unknown as Record<string, unknown>);
  return {
    fired: present,
    detail: present
      ? `window.${name} is defined (typeof ${typeof (window as any)[name]})`
      : `window.${name} is not defined`,
  };
}

/**
 * Probe for an attribute on the document root.
 *
 * Extensions that restyle the whole page often mark it so they can detect their own
 * work on re-entry. Those markers are usually vendor-namespaced, which makes them
 * uniquely identifying rather than merely category-identifying.
 */
export async function probeRootAttribute(attribute: string): Promise<SignalOutcome> {
  const value = document.documentElement.getAttribute(attribute);
  return {
    fired: value !== null,
    detail:
      value !== null
        ? `<html ${attribute}="${value}">`
        : `<html> has no ${attribute} attribute`,
  };
}

/**
 * Probe for attributes with a given prefix on <body> (or any element).
 *
 * Several extensions stamp a vendor-prefixed marker on the body so they can recognise
 * their own initialisation — Grammarly's `data-gr-ext-installed`, Monica's `monica-id`.
 * Prefix matching survives version suffixes that an exact-name check would miss.
 */
export async function probeAttributePrefix(
  prefix: string,
  { target = 'body' }: { target?: 'body' | 'html' } = {},
): Promise<SignalOutcome> {
  const el = target === 'body' ? document.body : document.documentElement;
  const found = [...el.attributes].map((a) => a.name).filter((n) => n.startsWith(prefix));
  return {
    fired: found.length > 0,
    detail: found.length
      ? `<${target}> carries ${found.join(', ')}`
      : `<${target}> has no ${prefix}* attribute`,
  };
}

/**
 * Insert bait into the page and see whether an extension reacts to it.
 *
 * This is the general form of the "load a dummy ad and see if it disappears" trick.
 * Extensions only act when they see something worth acting on — a password field, an
 * editable region, a styled element — so a probe has to present that first, then wait
 * for the reaction. The bait is removed afterwards, so the host page is unchanged.
 *
 * `check` receives the bait element after the wait and decides whether the extension
 * touched it.
 */
export function probeBait(
  html: string,
  check: (el: HTMLElement) => SignalOutcome,
  { waitMs = 1500 }: { waitMs?: number } = {},
): Promise<SignalOutcome> {
  return new Promise((resolve) => {
    const host = document.createElement('div');
    // Off-screen rather than display:none — an extension that skips invisible elements
    // would otherwise never take the bait.
    host.style.cssText = 'position:absolute;left:-9999px;top:0;width:400px;height:300px';
    host.innerHTML = html;
    document.body.appendChild(host);

    const bait = host.firstElementChild as HTMLElement | null;
    if (!bait) {
      host.remove();
      resolve({ fired: false, detail: 'bait markup produced no element' });
      return;
    }

    setTimeout(() => {
      let outcome: SignalOutcome;
      try {
        outcome = check(bait);
      } catch (err) {
        outcome = { fired: false, detail: `bait check threw: ${(err as Error).message}` };
      }
      host.remove();
      resolve(outcome);
    }, waitMs);
  });
}

/** Names of attributes an extension added to the bait, matched by prefix. */
export function baitAttributesWithPrefix(el: HTMLElement, prefix: string): string[] {
  return [...el.attributes].map((a) => a.name).filter((n) => n.startsWith(prefix));
}

/** Probe for a DOM node matching a selector, optionally waiting for it to appear. */
export function probeSelector(
  selector: string,
  { timeoutMs = 1500 }: { timeoutMs?: number } = {},
): Promise<SignalOutcome> {
  const found = (el: Element): SignalOutcome => ({
    fired: true,
    detail: `matched ${selector} -> <${el.tagName.toLowerCase()}>`,
  });

  const existing = document.querySelector(selector);
  if (existing) return Promise.resolve(found(existing));

  // Content scripts often inject after load, so watch briefly rather than sampling once.
  const attempt = new Promise<SignalOutcome>((resolve) => {
    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        observer.disconnect();
        resolve(found(el));
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
    setTimeout(() => {
      observer.disconnect();
      resolve({ fired: false, detail: `no node matched ${selector} within ${timeoutMs}ms` });
    }, timeoutMs);
  });

  return attempt;
}

/**
 * Present a login form as bait and check whether an extension reacts by injecting a
 * matching element *anywhere* in the document.
 *
 * Password managers only act when they see a login form, and they inject their UI as a
 * detached sibling rather than onto the field — so a plain `probeSelector` on an
 * ordinary page never sees it. This presents username + password inputs, drives them the
 * way a user would (focus, pointer, click), waits for the reaction, then queries the
 * whole document for `selector`. The bait is removed afterwards, so the host page is
 * unchanged.
 *
 * Only fires while the extension is actively reacting, so — like all behavioural signals
 * — a negative is weaker than a resource-probe negative.
 */
export function probeLoginBait(
  selector: string,
  { waitMs = 4000 }: { waitMs?: number } = {},
): Promise<SignalOutcome> {
  return new Promise((resolve) => {
    const host = document.createElement('div');
    // On-screen and genuinely visible: password managers skip fields that are off-screen
    // (`left:-9999px`) OR near-transparent (`opacity:0.01`) — both were verified to make
    // RoboForm ignore the form. So it is pinned top-left, opaque, at a high z-index, and
    // removed the moment the marker appears (typically ~600ms) to keep the flash brief.
    host.style.cssText = 'position:fixed;top:0;left:0;width:240px;z-index:2147483646';
    host.innerHTML =
      '<form action="/login" method="post">' +
      '<input type="text" name="username" autocomplete="username">' +
      '<input type="password" name="password" autocomplete="current-password">' +
      '<button type="submit">Sign in</button></form>';
    document.body.appendChild(host);

    const fields = [...host.querySelectorAll('input')] as HTMLInputElement[];
    for (const el of fields) {
      el.dispatchEvent(new MouseEvent('mouseover', { bubbles: true }));
      el.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
      el.focus();
      el.dispatchEvent(new MouseEvent('click', { bubbles: true }));
      el.dispatchEvent(new Event('input', { bubbles: true }));
    }

    const start = performance.now();
    const done = (fired: boolean) => {
      clearInterval(timer);
      host.remove();
      resolve({
        fired,
        detail: fired
          ? `login bait triggered ${selector}`
          : `no ${selector} after presenting a login form`,
      });
    };
    // Poll rather than a single wait, and exit early on first match to minimise the flash.
    const timer = setInterval(() => {
      if (document.querySelector(selector)) done(true);
      else if (performance.now() - start > waitMs) done(false);
    }, 200);
  });
}
